import BasicModal from '../common/BasicModal';
import { FC, useContext, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import InfoIcon from '@mui/icons-material/Info';
import { CampaignContext } from '../../contexts/CampaignContext';
import { Box, Button, Checkbox, FormControl, FormControlLabel, TextField, Typography, Paper, Slider, IconButton } from '@mui/material';
import axios from 'axios';
import IResponse from '../../interfaces/response';
import { IMediaGrothPanel } from '../../interfaces/campaign';
import { DataGrid, GridColDef } from '@mui/x-data-grid';
import i18next from 'i18next';
import { interpolatePrice, interpolateView } from '../../utils/interpolate';
import { formatNumberWithComma } from '../../utils/number';
import AddIcon from '@mui/icons-material/Add';
import DoneIcon from '@mui/icons-material/Done';

type IAddPostModal = {
	open: boolean;
	onClose: () => void;
	handleSaveNotSocial: (e: any) => void;
	view: number;
	onChangeView: (newValue: number) => void;
	selectedBudget: number;
	onBudgetChange: (newValue: number) => void;
};

const MediaGrowthModal: FC<IAddPostModal> = ({ open, onClose, handleSaveNotSocial, view, onChangeView, selectedBudget, onBudgetChange }) => {
	const { t } = useTranslation();
	const { selectedPlatform, title, description, selectedPlatformId, selectedCampaignId, contentStatus, selectedContentType, selectedMediaTypeId, setTitle, setDescription } = useContext(CampaignContext);
	const [files, setFiles] = useState<(File | string)[]>([]);
	const [panelPrice, setPanelPrice] = useState<IMediaGrothPanel[]>([]);
	const [text, setText] = useState<string>('');
	const [link, setLink] = useState<string>('');
	const [social, setSocial] = useState<string[]>([]);
	const [finalClickInfo, setFinalClickInfo] = useState<any>(null);
	const [viewMarks, setViewMarks] = useState<{ view: number; price: number }[]>([]);
	useEffect(() => {
		if (selectedPlatform === 'media_growth') {
			setTitle('');
			setFiles([]);
			setPanelPrice([]);
			setLink('');
			setText('');
			setDescription('');
			setViewMarks([]);
		}
	}, [selectedPlatform, selectedMediaTypeId, selectedContentType]);

	const handleCheckboxChange = (event: React.ChangeEvent<HTMLInputElement>) => {
		const { value, checked } = event.target;
		if (checked) {
			setSocial([...social, value]);
		} else {
			setSocial(social.filter(val => val !== value));
		}
	};

	const columns: GridColDef[] = [
		{
			field: 'action',
			align: 'center',
			headerAlign: 'center',
			headerName: i18next.t('action'),
			renderCell: (params: any) => {
				let data = params.row;
				return (
					<>
						<IconButton onClick={() => setFinalClickInfo(data)}>
							{finalClickInfo && finalClickInfo.id === data.id ? (
								<Button className="text-start" size="small">
									<DoneIcon className="text-text-white" />
								</Button>
							) : (
								<Button className="text-start" size="small">
									<AddIcon className="text-text-white" />
								</Button>
							)}
						</IconButton>
					</>
				);
			},
		},
		{ field: 'title', headerName: i18next.t('panel'), width: 110 },
		{ field: 'price', headerName: i18next.t('price'), width: 110 },
		{ field: 'followers', headerName: i18next.t('Estimation_of_follower_acquisition'), width: 110 },
	];
	let params = {
		id: null,
		campaign_id: selectedCampaignId,
		content_status: contentStatus,
		text: text,
		link: link,
		title: title,
		description: description,
		social: social,
		images: files,
		table: finalClickInfo,
		media_id: selectedPlatformId,
		media_type_id: selectedPlatform === 'sms' ? 'sms' : selectedMediaTypeId,
		absorption_amount: finalClickInfo?.followers,
		price: finalClickInfo?.price ?? selectedBudget,
		panel_id: finalClickInfo?.id ?? '',
		absorptionRate: view,
	};
	useEffect(() => {
		axios<IResponse<IMediaGrothPanel[]>>({
			method: 'get',
			url: '/campaigns/panels',
		}).then(async res => {
			if (res.data.success) {
				setPanelPrice(res.data.data);
			}
		});

		if (selectedPlatformId) {
			axios<IResponse<any>>({
				method: 'get',
				url: '/campaigns/view-prices',
				params: {
					media_id: selectedPlatformId,
					type: 'required_add',
				},
			}).then(res => {
				if (res.data.success) {
					setViewMarks(res?.data.data);
				}
			});
		}
	}, [selectedMediaTypeId]);

	const handleChangeView = (_: Event, newValue: number | number[]) => {
		if (typeof newValue === 'number') {
			onChangeView(newValue);
			onBudgetChange(interpolatePrice(newValue, viewMarks) ?? 0); // Update budget when slider changes
		}
	};

	const handleBudgetChange = (e: React.ChangeEvent<HTMLInputElement>) => {
		const value = e.target.value.replace(/,/g, '');
		const newBudget = Number(value);
		onBudgetChange(newBudget);
		onChangeView(interpolateView(newBudget, viewMarks) ?? viewMarks[0].view);
	};
	const formatTooltip = (value: number) => {
		return `${value.toLocaleString()} هزار بازدید`;
	};

	return (
		<BasicModal
			className={`h-auto w-4/12`}
			open={open}
			onClose={onClose}
			titleKey={`${selectedMediaTypeId === 'popup' ? 'popup' : 'required_add'}`}
			titleValues={{
				contentType: t(selectedContentType as string),
			}}
		>
			{selectedMediaTypeId === 'popup' ? (
				<>
					<FormControl fullWidth>
						<div className="flex w-full flex-col items-center justify-around gap-x-4 pt-8">
							<TextField onChange={e => setTitle(e.target.value)} placeholder={t('title')} fullWidth className="my-2" />
							<TextField onChange={e => setLink(e.target.value)} placeholder={t('channel_link')} fullWidth className="my-2" />
							<TextField placeholder={t('description_of_how_to_run')} onChange={e => setDescription(e.target.value)} className="my-2" multiline fullWidth rows={3} maxRows={4} />
							<Box className="my-3 flex items-center justify-start">
								<FormControlLabel value={t('instagram')} control={<Checkbox onChange={handleCheckboxChange} />} label={t('instagram')} />
								<FormControlLabel value={t('eita')} control={<Checkbox onChange={handleCheckboxChange} />} label={t('eita')} />
								<FormControlLabel value={t('rubika')} control={<Checkbox onChange={handleCheckboxChange} />} label={t('rubika')} />
							</Box>
							<Box className="mb-5 flex w-full items-center rounded-lg bg-blue-100 p-2.5 text-blue-500">
								<InfoIcon color="primary" />
								<Typography>{t('for_eita_channel_authentication_is_necessary')}</Typography>
							</Box>
							<Paper className="w-full">
								<DataGrid rows={panelPrice} hideFooterPagination hideFooterSelectedRowCount columns={columns} sx={{ border: 0 }} />
							</Paper>
						</div>
					</FormControl>
				</>
			) : (
				<>
					<FormControl fullWidth>
						<div className="flex w-full flex-wrap justify-around gap-x-4 pt-8">
							<TextField placeholder={t('channel_link')} value={link} onChange={e => setLink(e.target.value)} fullWidth className="my-2" />
							<TextField placeholder={t('order_details')} value={text} onChange={e => setText(e.target.value)} className="my-2" multiline fullWidth rows={4} maxRows={4} />
							<TextField placeholder={t('price')} value={formatNumberWithComma(selectedBudget)} onChange={handleBudgetChange} fullWidth className="my-2" />
						</div>
					</FormControl>
					<Box className="w-full">
						<Typography className="mt-2" id="input-slider" gutterBottom>
							{t('absorption_rate')}
						</Typography>
						<Slider size="small" step={1000} min={viewMarks[0]?.view} max={viewMarks[viewMarks.length - 1]?.view} className="min-w-52" marks={viewMarks?.map(viewMark => ({ value: viewMark?.view, label: viewMark.view?.toLocaleString() }))} value={view} onChange={handleChangeView} valueLabelDisplay="on" valueLabelFormat={formatTooltip} />
					</Box>
				</>
			)}
			<Button
				className="mt-5"
				onClick={() => {
					handleSaveNotSocial(params);
				}}
				fullWidth
			>
				{t('register')}
			</Button>
		</BasicModal>
	);
};

export default MediaGrowthModal;
